import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    inject,
} from '@angular/core';
import {Router} from '@angular/router';
import {Subject, Subscription, debounceTime, distinctUntilChanged} from 'rxjs';
import {
    UiTableColumn,
    UiTableAction,
    UiTableActionEvent,
    UiTablePageEvent,
    UiTableRow,
    UiTableSearchEvent,
    UiTableSortEvent,
    UiTableConfig,
} from 'src/app/shared/models/ui-table.model';
import {MenuItem} from 'primeng/api';
import {Menu} from 'primeng/menu';
import {SeverityService} from 'src/app/core/services/severity.service';

@Component({
    selector: 'ui-table',
    templateUrl: './ui-table.component.html',
    styleUrls: ['./ui-table.component.scss'],
    standalone: false
})
export class UiTableComponent implements OnInit, OnDestroy, OnChanges {
    @ViewChild('actionsMenu') actionsMenu!: Menu;
    private router = inject(Router);
    severityService = inject(SeverityService);
    @Input() tableConfig!: UiTableConfig;
    @Input() data: UiTableRow[] = [];
    @Input() globalFilterFields: string[] = [];
    @Input() paginator = true;
    @Input() rows = 10;
    @Input() rowsPerPageOptions: number[] = [10, 25, 50];
    @Input() rowsPerPageLabel = 'righe';
    @Input() totalRecords: number | null = null;
    @Input() emptyMessage = 'Nessun dato trovato';
    /** Abilita il click sulla riga: di default le righe non sono navigabili. */
    @Input() rowClickable = false;
    @Output() rowClick = new EventEmitter<UiTableRow>();
    @Output() searchChange = new EventEmitter<UiTableSearchEvent>();
    @Output() sortChange = new EventEmitter<UiTableSortEvent>();
    @Output() pageChange = new EventEmitter<UiTablePageEvent>();
    @Output() actionClick = new EventEmitter<UiTableActionEvent>();
    activeSortBy: string | null = null;
    activeSort: 'asc' | 'desc' | null = null;
    private searchQuery$ = new Subject<string>();
    private searchSubscription: Subscription | null = null;
    private currentRowForActions: UiTableRow | null = null;
    multipleActionsMenuItems: MenuItem[] = [];
    rowsPerPageDropdownOptions: { label: string; value: number }[] = [];
    currentRows = 10;
    currentFirst = 0;

    get resolvedGlobalFilterFields(): string[] {
        if (this.globalFilterFields.length > 0) {
            return this.globalFilterFields;
        }

        return this.tableConfig.columns.map((column) => column.field);
    }

    ngOnInit(): void {
        this.currentRows = this.rows;
        this.buildRowsPerPageDropdownOptions();
        this.normalizeCurrentRows();
        this.buildMultipleActionsMenuItems();

        this.searchSubscription = this.searchQuery$
            .pipe(debounceTime(600), distinctUntilChanged())
            .subscribe((query) => {
                const normalizedQuery = query.trim();
                if (normalizedQuery.length !== 0 && normalizedQuery.length < 3) {
                    return;
                }

                const searchEvent: UiTableSearchEvent = {query: normalizedQuery};
                this.searchChange.emit(searchEvent);
            });
    }

    ngOnDestroy(): void {
        this.searchSubscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['actions']) {
            this.buildMultipleActionsMenuItems();
        }

        if (changes['rowsPerPageOptions'] || changes['rowsPerPageLabel']) {
            this.buildRowsPerPageDropdownOptions();
        }

        if (changes['rows'] || changes['rowsPerPageOptions'] || changes['rowsPerPageLabel']) {
            this.currentRows = this.rows;
            this.normalizeCurrentRows();
        }

        if (
            changes['data'] ||
            changes['totalRecords'] ||
            changes['rows'] ||
            changes['rowsPerPageOptions'] ||
            changes['rowsPerPageLabel']
        ) {
            this.ensureFirstWithinBounds();
        }
    }

    onRowClick(row: UiTableRow): void {
        if (!this.rowClickable) {
            return;
        }
        this.rowClick.emit(row);
    }

    onGlobalFilter(event: Event): void {
        const value = (event.target as HTMLInputElement | null)?.value ?? '';
        this.searchQuery$.next(value);
    }

    onColumnSortClick(column: UiTableColumn): void {
        if (column.sortable === false) {
            return;
        }

        if (this.activeSortBy !== column.field) {
            this.activeSortBy = column.field;
            this.activeSort = 'asc';
        } else if (this.activeSort === 'asc') {
            this.activeSort = 'desc';
        } else {
            this.activeSortBy = null;
            this.activeSort = null;
        }

        const sortEvent: UiTableSortEvent = {
            sort_by: this.activeSortBy,
            sort: this.activeSort,
        };
        this.sortChange.emit(sortEvent);
    }

    /**
     * Stato dell'ordinamento per la colonna, annunciato dall'intestazione.
     * `none` va dichiarato sulle colonne ordinabili non attive, così la
     * tecnologia assistiva sa che l'ordinamento è possibile.
     */
    getAriaSort(column: UiTableColumn): 'ascending' | 'descending' | 'none' | null {
        if (column.sortable === false) {
            return null;
        }
        if (this.activeSortBy !== column.field || this.activeSort === null) {
            return 'none';
        }
        return this.activeSort === 'asc' ? 'ascending' : 'descending';
    }

    /** Dice cosa fa il comando, non com'è ordinato adesso. */
    getSortActionLabel(column: UiTableColumn): string {
        const next =
            this.activeSortBy === column.field && this.activeSort === 'asc'
                ? 'decrescente'
                : 'crescente';
        return `Ordina per ${column.header}, ${next}`;
    }

    /**
     * Nome del comando di riga. Con l'etichetta visibile serve solo se manca
     * un nome esplicito; con la sola icona è l'unico nome disponibile.
     */
    getActionAriaLabel(actionItem: UiTableAction): string | undefined {
        return actionItem.ariaLabel ?? actionItem.label ?? undefined;
    }

    getSortIconClass(column: UiTableColumn): string {
        if (this.activeSortBy !== column.field || this.activeSort === null) {
            return 'pi pi-sort-alt';
        }

        return this.activeSort === 'asc' ? 'pi pi-sort-amount-up-alt' : 'pi pi-sort-amount-down';
    }

    onPageChange(event: { first: number; rows: number }): void {
        this.currentRows = event.rows;
        this.currentFirst = event.first;

        const pageEvent: UiTablePageEvent = {
            page: event.rows > 0 ? Math.floor(event.first / event.rows) : 0,
            rows: event.rows,
        };
        this.pageChange.emit(pageEvent);
    }

    onRowsPerPageChange(rows: number): void {
        this.currentRows = rows;
        this.currentFirst = 0;

        const pageEvent: UiTablePageEvent = {
            page: 0,
            rows,
        };
        this.pageChange.emit(pageEvent);
    }

    onRowsPerPageDropdownChange(event: { value: number }): void {
        this.onRowsPerPageChange(event.value);
    }

    get singleActions(): UiTableAction[] {
        return this.tableConfig.actions.filter((a) => a.type === 'single');
    }

    get multipleActions(): UiTableAction[] {
        return this.tableConfig.actions.filter((a) => a.type === 'multiple');
    }

    buildMultipleActionsMenuItems(): void {
        this.multipleActionsMenuItems = this.multipleActions.map((actionItem) => ({
            label: actionItem.label,
            icon: actionItem.icon,
            styleClass: actionItem.styleClass,
            command: () => {
                if (!this.currentRowForActions) return;
                const actionKey = actionItem.action ?? actionItem.label ?? '';
                if (!actionKey) return;

                const payload: UiTableActionEvent = {
                    action: actionKey,
                    row: this.currentRowForActions,
                };
                this.actionClick.emit(payload);
            },
        }));
    }

    openActionsMenu(event: Event, row: UiTableRow): void {
        if (this.multipleActions.length === 0) return;
        this.currentRowForActions = row;
        this.actionsMenu.toggle(event);
    }

    onSingleActionClick(actionItem: UiTableAction, row: UiTableRow): void {
        const actionKey = actionItem.action ?? actionItem.label ?? '';
        if (!actionKey) return;

        const payload: UiTableActionEvent = {
            action: actionKey,
            row,
        };

        this.actionClick.emit(payload);
    }

    hasActionContent(actionItem: UiTableAction): boolean {
        return Boolean(actionItem.label || actionItem.icon);
    }

    getSingleActionStyleClass(actionItem: UiTableAction): string {
        return ['ui-table-action-btn', actionItem.styleClass ?? ''].join(' ').trim();
    }

    private normalizeCurrentRows(): void {
        if (this.rowsPerPageOptions.length === 0) {
            return;
        }

        if (!this.rowsPerPageOptions.includes(this.currentRows)) {
            this.currentRows = this.rowsPerPageOptions[0];
        }
    }

    private buildRowsPerPageDropdownOptions(): void {
        this.rowsPerPageDropdownOptions = this.rowsPerPageOptions.map((rowsOption) => ({
            label: `${rowsOption} ${this.rowsPerPageLabel}`,
            value: rowsOption,
        }));
    }

    private ensureFirstWithinBounds(): void {
        const records = this.resolvedTotalRecords;
        if (records <= 0 || this.currentRows <= 0) {
            this.currentFirst = 0;
            return;
        }

        const lastPageFirst = Math.floor((records - 1) / this.currentRows) * this.currentRows;
        if (this.currentFirst > lastPageFirst) {
            this.currentFirst = lastPageFirst;
        }
    }

    get resolvedTotalRecords(): number {
        return this.totalRecords ?? this.data.length;
    }

    get isWidgetTable(): boolean {
        return this.tableConfig?.tableType === 'widget';
    }

    get widgetRedirectLabel(): string {
        const title = (this.tableConfig?.title ?? '').trim();
        if (!title) return 'Vedi tutte';
        return `Vedi tutte le ${title.toLowerCase()}`;
    }

    onRedirectClick(link: string): void {
        this.router.navigate([link]);
    }
}
